import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getImage } from '../utils/imageCache';

function Navbar({ theme, handleThemeChange, profileData, signOut, info, user }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cachedProfilePicture, setCachedProfilePicture] = useState(null);
  const navigate = useNavigate();

  const isAuthenticated = !!user;
  
  // Load cached profile picture when profileData changes
  useEffect(() => {
    if (profileData?.profilePictureUrl) {
      getImage(profileData.profilePictureUrl).then(cachedImage => {
        if (cachedImage) {
          setCachedProfilePicture(cachedImage);
        }
      });
    }
  }, [profileData]);

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleSignOut = () => {
    closeDropdown();
    closeMenu();
    signOut();
  };

  const handleSignIn = () => {
    closeDropdown();
    closeMenu();
    // Amplify will handle the sign in flow
    navigate('/');
  };

  const handleProfileClick = () => {
    closeDropdown();
    closeMenu();
    navigate('/profile');
  };

  return (
    <div className="navbar bg-base-300">
      {/* Mobile menu toggle button */}
      <div className="navbar-start">
        <div className="dropdown lg:hidden">
          <div tabIndex="0" role="button" className="btn btn-ghost" onClick={toggleMenu}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16" />
            </svg>
          </div>
          <ul tabIndex="0" className={`menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-200 rounded-box w-52 ${isMenuOpen ? '' : 'hidden'}`}>
            <li><Link to="/" onClick={closeMenu}>Home</Link></li>
            <li><Link to="/subscribe" onClick={closeMenu}>Pricing</Link></li>
            {isAuthenticated && (
              <>
                <li><Link to="/chat" onClick={closeMenu}>Chat</Link></li>
                <li><Link to="/oranges" onClick={closeMenu}>Oranges</Link></li>
              </>
            )}
          </ul>
        </div>
        <Link to="/" className="btn btn-ghost text-xl">Custom App</Link>
      </div>
      
      {/* Desktop menu */}
      <div className="navbar-center hidden lg:flex">
        <ul className="menu menu-horizontal px-1">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/subscribe">Pricing</Link></li>
          {isAuthenticated && (
            <>
              <li><Link to="/chat">Chat</Link></li>
              <li><Link to="/oranges">Oranges</Link></li>
            </>
          )}
        </ul>
      </div>
      <div className="navbar-end gap-2">
        <div className="dropdown dropdown-end">
          <div tabIndex="0" role="button" className="btn btn-ghost btn-sm md:btn-md">
            <span className="hidden sm:inline">Theme: {theme}</span>
            <span className="inline sm:hidden">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
              </svg>
            </span>
          </div>
          <ul tabIndex="0" className="dropdown-content z-[1] p-2 shadow-lg bg-base-200 rounded-box w-52 max-h-[70vh] overflow-y-auto">
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('light')}>Light</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('dark')}>Dark</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('cupcake')}>Cupcake</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('bumblebee')}>Bumblebee</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('emerald')}>Emerald</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('corporate')}>Corporate</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('synthwave')}>Synthwave</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('cyberpunk')}>Cyberpunk</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('valentine')}>Valentine</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('halloween')}>Halloween</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('garden')}>Garden</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('forest')}>Forest</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('aqua')}>Aqua</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('lofi')}>Lofi</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('pastel')}>Pastel</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('fantasy')}>Fantasy</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('wireframe')}>Wireframe</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('black')}>Black</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('luxury')}>Luxury</button></li>
            <li><button className="btn btn-sm btn-block" onClick={() => handleThemeChange('dracula')}>Dracula</button></li>
          </ul>
        </div>
        {isAuthenticated ? (
          <div className="dropdown dropdown-end">
            <div 
              tabIndex="0" 
              role="button" 
              className="avatar cursor-pointer" 
              onClick={toggleDropdown}
            >
              <div className="w-10 rounded-full">
                {cachedProfilePicture ? (
                  <img src={cachedProfilePicture} alt="Profile" />
                ) : (
                  <div className="bg-primary text-neutral-content rounded-full w-10 h-10 flex items-center justify-center">
                    <span>{(info?.username?.[0] || user?.username?.[0] || 'U').toUpperCase()}</span>
                  </div>
                )}
              </div>
            </div>
            {isDropdownOpen && (
              <ul 
                tabIndex="0" 
                className="dropdown-content z-[1] menu p-2 shadow bg-base-200 rounded-box w-52"
              >
                <li><button onClick={handleProfileClick}>Profile</button></li>
                <li><button onClick={handleSignOut}>Sign out</button></li>
              </ul>
            )}
          </div>
        ) : (
          <div className="dropdown dropdown-end">
            <div 
              tabIndex="0" 
              role="button" 
              className="avatar cursor-pointer" 
              onClick={toggleDropdown}
            >
              <div className="bg-gray-400 text-neutral-content rounded-full w-10 h-10 flex items-center justify-center">
                <span>?</span>
              </div>
            </div>
            {isDropdownOpen && (
              <ul 
                tabIndex="0" 
                className="dropdown-content z-[1] menu p-2 shadow bg-base-200 rounded-box w-52"
              >
                <li><button onClick={handleSignIn}>Sign in</button></li>
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Navbar;
